import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import AddIcon from '@atlaskit/icon/core/add';
import Heading from '@atlaskit/heading/heading';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import {
	ANALYTICS_CREATE_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../../hooks/analytics/useSnippetAnalytics.ts';
import { CreateSnippetModal } from '../../modals/CreateSnippetModal.tsx';
import { SnippetPreviewContainer } from '../preview/SnippetPreviewContainer.tsx';
import { useSnippetPopupPreview } from '../preview/SnippetPopupPreviewContext.tsx';

import { SnippetPanelCard } from './SnippetPanelCard.tsx';

const messages = defineMessages({
	newBlockTemplateTitle: {
		id: 'snippets.panel.createItem.newBlockTemplate',
		defaultMessage: 'New block template',
		description: 'Title for the create block template row in the snippet panel list',
	},
	cardAriaLabel: {
		id: 'snippets.panel.createItem.cardAriaLabel',
		defaultMessage: 'New block template',
		description: 'Accessible label for the create block template card button',
	},
});

const titleStyles = cssMap({
	titleContainer: {
		flex: 1,
		textAlign: 'left',
	},
	createIconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
	},
	createIconScaled: {
		transform: 'scale(2)',
		transformOrigin: 'center',
	},
});

type SnippetPanelCreateItemProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelCreateItem = ({
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelCreateItemProps): React.JSX.Element => {
	const intl = useIntl();
	const { editorAPI } = useContext(EditorContextPanelContext);
	const { showModal } = useDialogs();
	const { fireButtonClickedAnalytic } = useSnippetAnalytics(ANALYTICS_ORIGIN.SNIPPET_PANEL);
	const { hidePreview } = useSnippetPopupPreview();

	const onClick = () => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET, {
			createSource: ANALYTICS_CREATE_SOURCE.SNIPPET_PANEL_LIST,
		});
		showModal(CreateSnippetModal, {
			contentId,
			spaceId,
			spaceKey,
			editorAPI: editorAPI ?? null,
			analyticsOrigin: ANALYTICS_ORIGIN.SNIPPET_PANEL,
			createSource: ANALYTICS_CREATE_SOURCE.SNIPPET_PANEL_LIST,
		});
	};

	return (
		<SnippetPanelCard
			onClick={onClick}
			onMouseEnter={hidePreview}
			ariaLabel={intl.formatMessage(messages.cardAriaLabel)}
			testId="snippet-panel-create-item-button"
		>
			<SnippetPreviewContainer>
				<Box xcss={titleStyles.createIconWrapper}>
					<Box xcss={titleStyles.createIconScaled}>
						<AddIcon
							size="medium"
							label={intl.formatMessage(messages.newBlockTemplateTitle)}
							color={token('color.icon.subtle')}
						/>
					</Box>
				</Box>
			</SnippetPreviewContainer>
			<Stack alignInline="start" alignBlock="start" space="space.075" grow="fill">
				<Box xcss={titleStyles.titleContainer}>
					<Heading size="xsmall">
						<Text maxLines={3} weight="bold">
							{intl.formatMessage(messages.newBlockTemplateTitle)}
						</Text>
					</Heading>
				</Box>
			</Stack>
		</SnippetPanelCard>
	);
};
